/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Edit } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableRow } from '../../components/ui/table';
import { format } from 'date-fns/format';
import { UserData } from '../../types/users/user.view';
import { apiUrl } from '../../config';
import dataProvider from '../../authentication/dataProvider';
import { Loader2, FileText } from 'lucide-react';
import KycReviewDialog from '../compliance/KycReviewDialog';

const UserViewActions = ({ onViewDocuments }: { onViewDocuments: () => void }) => {
  const navigate = useNavigate();

  return (
    <div className='flex items-center gap-4 mb-6'>
      <Button variant='outline' onClick={() => navigate(-1)}>
        <ArrowLeft className='h-4 w-4 mr-2' />
        Back
      </Button>
      <Button variant='secondary' onClick={onViewDocuments}>
        <FileText className='h-4 w-4 mr-2' />
        View Documents
      </Button>
    </div>
  );
};

const UserView = () => {
  const { id } = useParams();
  const [record, setRecord] = useState<UserData | null>(null);
  const [kycOpen, setKycOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    // Replace this with your real API call
    dataProvider
      .getOne('users', { id })
      .then((response) => setRecord(response.data))
      .catch((error) => {
        // Failed to fetch user data
      });
  }, [id]);

  if (!record)
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );

  return (
    <div className='p-4'>
      <UserViewActions onViewDocuments={() => setKycOpen(true)} />
      <Card>
        <CardContent className='pt-6'>
          <Tabs defaultValue='profile' className='w-full'>
            <TabsList className='grid w-full grid-cols-5'>
              <TabsTrigger value='profile'>Profile</TabsTrigger>
            </TabsList>

            <TabsContent value='profile'>
              <div className='space-y-6'>
                <div className='flex flex-col items-center gap-4 py-6'>
                  <Avatar className='h-32 w-32'>
                    <AvatarImage src={record?.profile} alt={record?.name} />
                    <AvatarFallback>{record?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </div>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className='font-medium w-[15%]'>ID</TableCell>
                      <TableCell className='w-[35%]'>{record?.id}</TableCell>
                      <TableCell className='font-medium w-[15%]'>Name</TableCell>
                      <TableCell className='w-[35%]'>{record?.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='font-medium w-[15%]'>Email</TableCell>
                      <TableCell className='w-[35%]'>{record?.email}</TableCell>
                      <TableCell className='font-medium w-[15%]'>Role ID</TableCell>
                      <TableCell className='w-[35%]'>{record?.role?.id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='font-medium w-[15%]'>Role Name</TableCell>
                      <TableCell className='w-[35%]'>{record?.role?.roleName}</TableCell>
                      <TableCell className='font-medium w-[15%]'>Wallet Address</TableCell>
                      <TableCell className='w-[35%] break-all'>{record?.walletAddress}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='font-medium w-[15%]'>Created At</TableCell>
                      <TableCell className='w-[35%]'>
                        {record?.createdAt && format(new Date(record.createdAt), 'PPP')}
                      </TableCell>
                      <TableCell className='font-medium w-[15%]'>Active Status</TableCell>
                      <TableCell className='w-[35%]'>
                        <Badge variant={record?.isActive ? 'success' : 'destructive'}>
                          {record?.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='font-medium w-[15%]'>Admin Acceptance</TableCell>
                      <TableCell className='w-[35%]'>
                        <Badge variant={record?.acceptedByAdmin ? 'success' : 'destructive'}>
                          {record?.acceptedByAdmin ? 'Accepted' : 'Not Accepted'}
                        </Badge>
                      </TableCell>
                      <TableCell className='font-medium w-[15%]'>Status</TableCell>
                      <TableCell className='w-[35%]'>{record?.status}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {id && <KycReviewDialog userId={id} open={kycOpen} onOpenChange={setKycOpen} />}
    </div>
  );
};

export default UserView;
